import { dizzyStarPixelSize, minimumDizzyStarSize } from "./dizzy-star-size";
import * as THREE from "three";
import { coloredGeometry } from "./backyard";
import { finishParticles, particleBatch, setParticle } from "./particles";
import { projectBounds } from "./game-renderer";
import { victoryPose } from "../ui/results";

// A broad contact core stays visible immediately beneath the base. The particle
// atlas's soft puff fades almost completely where the body meets the snow.
const contactShadow = (() => {
  const data = new Uint8Array(32 * 32 * 4);
  for (let y = 0; y < 32; y++)
    for (let x = 0; x < 32; x++) {
      const radius = Math.hypot((x + 0.5 - 16) / 16, (y + 0.5 - 16) / 16);
      const alpha = Math.min(1, Math.max(0, (1 - radius) / 0.25));
      data.set([255, 255, 255, Math.round(255 * alpha)], (y * 32 + x) * 4);
    }
  const texture = new THREE.DataTexture(data, 32, 32);
  texture.needsUpdate = true;
  return texture;
})();

export class ResultsStage extends THREE.Group {
  readonly confetti = new THREE.InstancedMesh(
    new THREE.PlaneGeometry(1, 1),
    new THREE.MeshBasicMaterial({ side: THREE.DoubleSide }),
    160,
  );
  readonly stars = particleBatch(27);
  readonly podium: THREE.Mesh;
  readonly rays: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  private readonly anchors = new Map<
    THREE.Object3D,
    { position: THREE.Vector3; rotation: THREE.Euler }
  >();
  readonly shadows = new THREE.InstancedMesh(
    new THREE.CircleGeometry(1, 20),
    new THREE.MeshBasicMaterial({
      map: contactShadow,
      color: 0x24213f,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
    }),
    2,
  );
  private readonly partTransforms = new Map<
    THREE.Object3D,
    { position: THREE.Vector3; scale: THREE.Vector3 }
  >();
  private sideOnSnow = false;
  visibleConfetti = 0;
  hero: THREE.Group;
  constructor(
    readonly player: THREE.Group,
    readonly opponent: THREE.Group,
    podium: THREE.Group,
  ) {
    super();
    this.hero = player;
    this.visible = false;
    this.podium = new THREE.Mesh(
      coloredGeometry(podium),
      new THREE.MeshBasicMaterial({ vertexColors: true }),
    );
    this.podium.position.z = 2.6;
    this.podium.scale.setScalar(0.82);
    this.add(this.podium, this.confetti, this.stars, this.shadows);
    this.confetti.frustumCulled = false;
    this.shadows.frustumCulled = false;
    this.confetti.count = 0;
    this.rays = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        uniforms: { turn: { value: 0 } },
        vertexShader:
          "varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}",
        fragmentShader:
          "uniform float turn;varying vec2 vUv;void main(){vec2 p=vUv-0.5;float a=atan(p.y,p.x)+turn;float ray=step(0.35,sin(a*18.0));float fade=max(0.0,1.0-length(p)*1.45);gl_FragColor=vec4(1.0,0.87,0.56,ray*fade*0.32);}",
      }),
    );
    this.add(this.rays);
    for (const root of [player, opponent])
      root.traverse((node) => {
        if (node.name.startsWith("face_") || node.name === "head")
          this.partTransforms.set(node, {
            position: node.position.clone(),
            scale: node.scale.clone(),
          });
        if (node.name.startsWith("anchor_"))
          this.anchors.set(node, {
            position: node.position.clone(),
            rotation: node.rotation.clone(),
          });
      });
  }
  private basePoint(root: THREE.Object3D): THREE.Vector3 {
    root.updateMatrixWorld(true);
    const base = new THREE.Vector3(0, Infinity, 0);
    const point = new THREE.Vector3();
    root.traverseVisible((node) => {
      if (
        !(node instanceof THREE.Mesh) ||
        /outline|shadow|ball/i.test(node.name)
      )
        return;
      const vertices = (
        node as THREE.Mesh<THREE.BufferGeometry>
      ).geometry.getAttribute("position");
      for (let i = 0; i < vertices.count; i++) {
        const p = point
          .fromBufferAttribute(vertices, i)
          .applyMatrix4(node.matrixWorld);
        if (p.y < base.y) base.copy(p);
      }
    });
    return base;
  }
  contactBounds(
    id: string,
    camera: THREE.Camera,
    width: number,
    height: number,
  ) {
    if (id === "topEllipse" && width <= height) return null;
    this.podium.updateMatrixWorld(true);
    if (!this.podium.geometry.boundingBox)
      this.podium.geometry.computeBoundingBox();
    const top = this.podium.geometry.boundingBox!.max.y;
    const points: THREE.Vector3[] = [];
    if (id === "sideShadow") {
      if (
        !this.shadows.visible ||
        this.shadows.count < 2 ||
        this.shadows.material.opacity !== 0.28
      )
        return null;
      const matrix = new THREE.Matrix4();
      this.shadows.getMatrixAt(1, matrix);
      for (let i = 0; i < 32; i++) {
        const a = (i * Math.PI) / 16;
        points.push(
          new THREE.Vector3(Math.cos(a), Math.sin(a), 0).applyMatrix4(matrix),
        );
      }
    } else if (id === "sideWall") {
      const box = this.podium.geometry.boundingBox!;
      // The front rim and the foot at the same X/Z isolate wall thickness.
      for (const y of [box.min.y, box.max.y])
        points.push(
          new THREE.Vector3(0, y, box.max.z).applyMatrix4(
            this.podium.matrixWorld,
          ),
        );
    } else if (id === "topFace" || (width > height && id === "topEllipse")) {
      const vertices = this.podium.geometry.getAttribute("position");
      for (let i = 0; i < vertices.count; i++) {
        const p = new THREE.Vector3().fromBufferAttribute(vertices, i);
        if (Math.abs(p.y - top) < 0.001)
          points.push(p.applyMatrix4(this.podium.matrixWorld));
      }
    } else {
      const root = id.startsWith("hero")
        ? this.hero
        : this.hero === this.player
          ? this.opponent
          : this.player;
      let base = this.basePoint(root);
      if (id === "sideSurface" && this.sideOnSnow) {
        base.y =
          this.podium.position.y +
          this.podium.geometry.boundingBox!.min.y * this.podium.scale.y;
      } else if (id.endsWith("Surface")) {
        const ray = new THREE.Raycaster(
          new THREE.Vector3(base.x, base.y + 10, base.z),
          new THREE.Vector3(0, -1, 0),
        );
        const hit = ray.intersectObject(this.podium, false)[0];
        if (!hit) return null;
        base = hit.point;
      }
      points.push(base);
    }
    const projected = points.map((p) => p.project(camera));
    if (width > height && id === "topEllipse") {
      // The top ring projects to a rotated ellipse off the optical axis.
      // Its screen-aligned bounding box overstates the minor axis. Sample the
      // actual ring uniformly, then recover its principal axes in screen pixels.
      const local = this.podium.geometry.getAttribute("position");
      const ring: THREE.Vector3[] = [];
      for (let i = 0; i < local.count; i++) {
        const p = new THREE.Vector3().fromBufferAttribute(local, i);
        if (
          Math.abs(p.y - top) < 0.001 &&
          Math.hypot(p.x, p.z) > 0.01 &&
          !ring.some((q) => q.distanceToSquared(p) < 1e-10)
        )
          ring.push(p);
      }
      const pixels = ring
        .map((p) => p.applyMatrix4(this.podium.matrixWorld).project(camera))
        .map((p) => ({ x: (p.x * width) / 2, y: (p.y * height) / 2 }));
      const cx = pixels.reduce((sum, p) => sum + p.x, 0) / pixels.length;
      const cy = pixels.reduce((sum, p) => sum + p.y, 0) / pixels.length;
      const xx =
        pixels.reduce((sum, p) => sum + (p.x - cx) ** 2, 0) / pixels.length;
      const yy =
        pixels.reduce((sum, p) => sum + (p.y - cy) ** 2, 0) / pixels.length;
      const xy =
        pixels.reduce((sum, p) => sum + (p.x - cx) * (p.y - cy), 0) /
        pixels.length;
      const minor = 2 * Math.sqrt(xx + yy - Math.hypot(xx - yy, 2 * xy));
      return {
        x: cx + width / 2,
        y: height / 2 - cy,
        width: 2 * Math.sqrt(xx + yy + Math.hypot(xx - yy, 2 * xy)),
        height: minor,
      };
    }
    const xs = projected.map((p) => ((p.x + 1) * width) / 2);
    const ys = projected.map((p) => ((1 - p.y) * height) / 2);
    return {
      x: Math.min(...xs),
      y: Math.min(...ys),
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys),
    };
  }
  restore(): void {
    for (const [node, base] of this.partTransforms) {
      node.position.copy(base.position);
      node.scale.copy(base.scale);
    }
    for (const [node, base] of this.anchors) {
      node.position.copy(base.position);
      node.rotation.copy(base.rotation);
    }
  }
  private face(root: THREE.Object3D, name: string): void {
    root.traverse((n) => {
      if (n.name.startsWith("face_")) n.visible = n.name === name;
    });
  }
  private hands(root: THREE.Object3D, y: number, wave = 0): void {
    for (const side of ["L", "R"]) {
      const node = root.getObjectByName(`anchor_mitten_${side}`);
      if (node) {
        // Keep the palm touching the body even while rotating at its anchor.
        node.position.x = side === "L" ? 0.53 : -0.53;
        node.position.y += y;
        node.rotation.x = side === "L" ? wave * 0.12 : 0;
        node.rotation.z = side === "L" ? (wave * 25 * Math.PI) / 180 : 0;
      }
    }
  }
  update(
    outcome: "win" | "lose" | "draw",
    t: number,
    camera: THREE.PerspectiveCamera,
    width: number,
    height: number,
    reduced: boolean,
    stageTop = 140,
    chipsTop = height,
    stageCenter = width / 2,
    pixelRatio = 1,
  ): void {
    this.restore();
    const landscape = width > height;
    const portraitPhone = width < height && !(width === 768 && height === 1024);
    const heroRatio = landscape ? 0.295 : outcome === "draw" ? 0.221 : 0.257;
    this.sideOnSnow = outcome !== "draw";
    // The opponent owns the podium on every loss; keep identity references fixed.
    const hero = outcome === "lose" ? this.opponent : this.player;
    this.hero = hero;
    const support = outcome === "lose" ? this.player : this.opponent;
    const celebratory = outcome !== "draw";
    const pose =
        outcome === "lose"
          ? {
              hop: 0,
              stretch: 0,
              spin: 0,
              wave: reduced ? 1 : 1 + 0.2 * Math.sin(t / 200),
            }
          : victoryPose(Math.max(0, t - 300), reduced),
      tablet = width / height > 0.62 ? 0.72 : 1;
    const scale =
      height < 700
        ? 0.74
        : height < 740
          ? 0.86
          : width / height > 0.62
            ? 0.75
            : 1.1;
    hero.position.set(0, 0.42 + (height < 740 ? 0.2 : 0), 2.6);
    this.podium.position.set(0, height < 740 ? 0.2 : 0, 2.6);
    hero.rotation.set(0, 0, 0);
    hero.scale.setScalar(scale);
    support.position.set(-1.45, 0, -0.9);
    support.rotation.set(0, 0, 0);
    support.scale.setScalar(tablet);
    this.podium.scale.setScalar(
      outcome === "draw" && !landscape ? 1.16 * 0.82 : 0.82,
    );
    if (landscape && outcome !== "draw") {
      this.podium.scale.y *= 1.6;
      this.podium.scale.z *= 1.025;
      this.podium.scale.x *= 1.1;
    }
    if (landscape && outcome === "draw") {
      // The top radius must cover both draw bases at X = ±0.85. Keep
      // enough depth exposed beside them and retain a solid front wall.
      this.podium.scale.multiplyScalar(0.84);
      this.podium.scale.x *= 2.6;
      this.podium.scale.z *= 3.0;
      this.podium.scale.y *= 2.1;
    }
    this.face(hero, "face_grin");
    this.face(
      support,
      outcome === "lose"
        ? "face_sad"
        : outcome === "win"
          ? "face_shocked"
          : "face_grin",
    );
    if (celebratory) {
      hero.rotation.y = pose.spin;
      hero.scale.set(
        scale * (1 + 0.12 * pose.stretch),
        scale * (1 - 0.1 * pose.stretch),
        scale,
      );
      this.hands(hero, 0, pose.wave);
      this.hands(support, -0.2);
      support.rotation.y = reduced
        ? 0
        : (Math.sin((t / 900) * Math.PI * 2) * 8 * Math.PI) / 180;
      const hat = support.getObjectByName("anchor_hat");
      if (hat) hat.rotation.z = outcome === "lose" ? 0 : (14 * Math.PI) / 180;
      if (outcome === "lose") {
        support.rotation.x = (8 * Math.PI) / 180;
        support.rotation.y = (12 * Math.PI) / 180;
      }
    } else {
      hero.position.x = 0.85;
      support.position.set(-0.85, hero.position.y, 2.6);
      hero.scale.setScalar(0.78 * scale);
      support.scale.setScalar(0.78 * scale);
      const shrug = reduced ? 0 : Math.sin((t / 700) * Math.PI * 2) * 0.05;
      this.hands(hero, shrug);
      this.hands(support, -shrug);
      hero.rotation.y = -Math.PI / 18;
      support.rotation.y = Math.PI / 18;
      if (t % 1400 >= 700) {
        this.face(hero, "face_determined");
        this.face(support, "face_determined");
      }
    }
    if (!this.podium.geometry.boundingBox)
      this.podium.geometry.computeBoundingBox();
    const localTop = this.podium.geometry.boundingBox!.max.y;
    const roots = [hero, support, this.podium];
    if (landscape && outcome === "draw") {
      // Share the top centerline so both bases stay inside the top rim.
      hero.position.z = this.podium.position.z;
      support.position.z = this.podium.position.z;
      hero.position.x = 0.85;
      support.position.x = -0.85;
    }
    const seat = (root: THREE.Object3D) => {
      root.updateMatrixWorld(true);
      const base = this.basePoint(root).y;
      root.position.y +=
        this.podium.position.y + localTop * this.podium.scale.y - base;
    };
    if (outcome !== "draw") {
      support.position.x = -1.45;
      support.position.z = this.podium.position.z + 0.25;
    }
    if (landscape) {
      // Keep both faces frontal throughout the landscape presentation.
      hero.rotation.set(
        0,
        Math.atan2(camera.position.x, camera.position.z - hero.position.z),
        0,
      );
      support.rotation.set(
        0,
        Math.atan2(camera.position.x, camera.position.z - support.position.z),
        0,
      );
    }
    // Start the draw pair larger relative to the wider podium; the shared
    // fit below still caps their final height at heroRatio.
    const initialHeroRatio = landscape && outcome === "draw" ? 0.6 : heroRatio;
    // Establish proportions once, then fit all three roots together.
    for (const root of [hero, support]) {
      const box = projectBounds(root, camera, width, height);
      root.scale.multiplyScalar(
        (height *
          (root === hero || (!landscape && outcome === "draw")
            ? initialHeroRatio
            : landscape
              ? heroRatio * 0.64
              : 0.145)) /
          box.height,
      );
    }
    if (landscape && outcome === "draw") support.scale.copy(hero.scale);
    // Resolve perspective at the seated height before fixing the ratio. The
    // original platform can also be too wide on compact screens.
    for (let pass = 0; outcome !== "draw" && pass < 8; pass++) {
      seat(hero);
      const heroBox = projectBounds(
        hero.getObjectByName("body") ?? hero,
        camera,
        width,
        height,
      );
      const topBox = this.contactBounds("topFace", camera, width, height)!;
      this.podium.scale.multiplyScalar(
        (heroBox.width * (landscape ? 1.25 : 1.17)) / topBox.width,
      );
    }
    for (let step = 0; step < 32; step++) {
      seat(hero);
      if (outcome === "draw") seat(support);
      else
        support.position.y +=
          this.podium.position.y +
          this.podium.geometry.boundingBox!.min.y * this.podium.scale.y -
          this.basePoint(support).y;
      if (outcome === "draw" && !landscape) {
        for (const [root, side] of [
          [hero, 1],
          [support, -1],
        ] as const) {
          const box = projectBounds(root, camera, width, height);
          const p = root.position.clone().project(camera);
          p.x +=
            (((landscape ? stageCenter : width / 2) +
              side * (width > height ? height * 0.1 : width * 0.22) -
              box.x -
              box.width / 2) *
              2) /
            width;
          root.position.copy(p.unproject(camera));
          seat(root);
        }
      }
      if (outcome !== "draw") {
        const box = projectBounds(support, camera, width, height);
        const p = support.position.clone().project(camera);
        const dx = Math.max(0, 16 - box.x);
        p.x += (dx * 2) / width;
        support.position.copy(p.unproject(camera));
      }
      const boxes = roots.map((root) =>
        projectBounds(root, camera, width, height),
      );
      const top = Math.min(...boxes.map((b) => b.y));
      const bottom = Math.max(...boxes.map((b) => b.y + b.height));
      const available = chipsTop - 8.2 - stageTop;
      const heroHeight = boxes[0]!.height;
      const factor = Math.min(
        available / (bottom - top),
        (height * heroRatio) / heroHeight,
      );
      if (Math.abs(factor - 1) > 0.00001) {
        const pivot = this.podium.position.clone();
        for (const root of roots) {
          root.position.sub(pivot).multiplyScalar(factor).add(pivot);
          root.scale.multiplyScalar(factor);
        }
      }
      const box = projectBounds(this.podium, camera, width, height);
      const p = this.podium.position.clone().project(camera);
      p.y += ((box.y + box.height - chipsTop + 8.2) * 2) / height;
      const delta = p.unproject(camera).sub(this.podium.position);
      for (const root of roots) root.position.add(delta);
    }
    if (landscape) {
      const boxes = roots.map((root) =>
        projectBounds(root, camera, width, height),
      );
      const left = Math.min(...boxes.map((b) => b.x));
      const right = Math.max(...boxes.map((b) => b.x + b.width));
      const p = this.podium.position.clone().project(camera);
      p.x += ((stageCenter - (left + right) / 2) * 2) / width;
      const delta = p.unproject(camera).sub(this.podium.position);
      for (const root of roots) root.position.add(delta);
    }
    const shadowDummy = new THREE.Object3D();
    for (let i = 0; i < 2; i++) {
      const root = i ? support : hero;
      shadowDummy.position.set(
        this.basePoint(root).x,
        this.podium.position.y +
          (i && outcome !== "draw"
            ? this.podium.geometry.boundingBox!.min.y * this.podium.scale.y
            : localTop * this.podium.scale.y) +
          0.002,
        this.basePoint(root).z,
      );
      shadowDummy.rotation.x = -Math.PI / 2;
      const bodySize = new THREE.Box3()
        .setFromObject(root.getObjectByName("body") ?? root)
        .getSize(new THREE.Vector3());
      // Keep the blob about 1.1 times the visible contact base width.
      // Its deeper footprint exposes the soft core below the low-angle base.
      shadowDummy.scale.set(
        (landscape && i && outcome !== "draw" ? 0.29 : 0.32) * bodySize.x,
        (landscape && i && outcome !== "draw" ? 1.1 : 0.2) * bodySize.z,
        1,
      );
      shadowDummy.updateMatrix();
      this.shadows.setMatrixAt(i, shadowDummy.matrix);
    }
    this.shadows.instanceMatrix.needsUpdate = true;
    camera.updateMatrixWorld(true);
    const dummy = new THREE.Object3D();
    this.stars.count =
      celebratory && support.getObjectByName("anchor_hat") ? 3 : 0;
    for (let i = 0; i < this.stars.count; i++) {
      const a =
        (i * Math.PI * 2) / 3 + (reduced ? 0 : (t / 1200) * Math.PI * 2);
      const hat = support.getObjectByName("anchor_hat")!;
      const supportBox = projectBounds(support, camera, width, height);
      const hatBox = projectBounds(hat, camera, width, height);
      const unit = supportBox.height / 150;
      const starPixels = landscape
        ? Math.max(
            8,
            projectBounds(
              support.getObjectByName("head") ?? hat,
              camera,
              width,
              height,
            ).width * 0.15,
          )
        : portraitPhone
          ? projectBounds(
              support.getObjectByName("head") ?? hat,
              camera,
              width,
              height,
            ).width * 0.225
          : 8 * unit;
      const center = support.position.clone().project(camera);
      center.x =
        ((hatBox.x + hatBox.width / 2 + Math.cos(a) * 14 * unit) / width) * 2 -
        1;
      center.y =
        1 -
        ((hatBox.y - 8 * unit - (landscape ? starPixels / 2 : 4 * unit)) /
          height) *
          2;
      dummy.position.copy(center.unproject(camera));
      dummy.quaternion.copy(camera.quaternion);
      const distance = dummy.position.distanceTo(camera.position);
      dummy.scale.setScalar(
        (starPixels * 2 * distance * Math.tan((camera.fov * Math.PI) / 360)) /
          height,
      );
      // Clamp the raster footprint without moving the existing star center.
      // The signed-off tablet transform remains untouched.
      if (landscape || portraitPhone) {
        // Match the raster footprint before changing size; already compliant
        // particles retain their exact transform.
        const projected = dummy.position.clone().project(camera);
        const depth = -dummy.position
          .clone()
          .applyMatrix4(camera.matrixWorldInverse).z;
        const projectedSize = (starPixels * distance) / depth;
        const minimumSize = minimumDizzyStarSize(
          ((projected.x + 1) / 2) * width * pixelRatio,
          ((1 - projected.y) / 2) * height * pixelRatio,
          projectedSize * pixelRatio,
        );
        if (minimumSize > projectedSize * pixelRatio)
          dummy.scale.multiplyScalar(
            minimumSize / (projectedSize * pixelRatio),
          );
      }
      dummy.updateMatrix();
      setParticle(
        this.stars,
        i,
        dummy.matrix,
        1,
        i % 2 ? 0x9ae5ff : 0xffd640,
        1,
      );
    }
    if (this.stars.count === 3) {
      // Work in screenshot pixels after the v2r20 size clamp. Move only X;
      // retain every scale, depth and Y, including the dizzy/hat anchor.
      const stars = [0, 1, 2]
        .map((i) => {
          const box = projectBounds(
            this.stars,
            camera,
            width,
            height,
            i + 1,
            i,
          );
          const x = (box.x + box.width / 2) * pixelRatio;
          const pixels = dizzyStarPixelSize(
            x,
            (box.y + box.height / 2) * pixelRatio,
            box.width * pixelRatio,
          );
          return { i, x, visibleWidth: pixels.w };
        })
        .sort((a, b) => a.x - b.x || a.i - b.i);
      const gaps = [0, 1].map(
        (i) =>
          stars[i + 1]!.x -
          stars[i]!.x -
          (stars[i]!.visibleWidth + stars[i + 1]!.visibleWidth) / 2,
      );
      if (gaps.some((gap) => gap < 3)) {
        // Five background pixels plus rounding room, proportional to the
        // enlarged visible footprint. Expand both ends by the same amount.
        const needed = gaps.map((gap) => Math.max(0, 5 - gap));
        let expansion = Math.ceil((needed[0]! + needed[1]!) / 2);
        while (
          Math.ceil(needed[0]! - expansion) > Math.floor(expansion - needed[1]!)
        )
          expansion++;
        const middleShift = Math.max(
          Math.ceil(needed[0]! - expansion),
          Math.min(Math.floor(expansion - needed[1]!), 0),
        );
        // Integer screenshot-pixel translations preserve each star's existing
        // raster footprint, including the strict six-pixel size minimum.
        const positions = [
          stars[0]!.x - expansion,
          stars[1]!.x + middleShift,
          stars[2]!.x + expansion,
        ];
        stars.forEach((star, rank) => {
          this.stars.getMatrixAt(star.i, dummy.matrix);
          const center = new THREE.Vector3().setFromMatrixPosition(
            dummy.matrix,
          );
          center.project(camera);
          center.x += ((positions[rank]! - star.x) / pixelRatio / width) * 2;
          center.unproject(camera);
          dummy.matrix.setPosition(center);
          this.stars.setMatrixAt(star.i, dummy.matrix);
        });
      }
    }
    if (outcome === "win" && !reduced && t >= 600 && t < 1200) {
      const age = (t - 600) / 1000;
      for (let i = 0; i < 24; i++) {
        const a = i * 2.39996,
          r = age * (1 + (i % 4) * 0.3);
        dummy.position.set(
          Math.cos(a) * r,
          0.5 + Math.sin(a) * r + age * 1.4,
          2.6 + Math.sin(i * 3) * r * 0.2,
        );
        dummy.quaternion.copy(camera.quaternion);
        dummy.scale.setScalar(0.08);
        dummy.updateMatrix();
        setParticle(
          this.stars,
          this.stars.count++,
          dummy.matrix,
          0,
          0xffffff,
          1 - age / 0.6,
        );
      }
    }
    finishParticles(this.stars);
    this.rays.visible = celebratory;
    const depth = 20,
      halfH = Math.tan((camera.fov * Math.PI) / 360) * depth;
    this.rays.position
      .set(0, (1 - 0.36 * 2) * halfH, -depth)
      .applyMatrix4(camera.matrixWorld);
    this.rays.quaternion.copy(camera.quaternion);
    this.rays.scale.set(halfH * camera.aspect * 2, halfH * 2, 1);
    this.rays.material.uniforms.turn!.value = reduced
      ? 0
      : ((t / 1000) * Math.PI) / 30;
    this.confetti.count = 0;
    this.visibleConfetti = 0;
    if (outcome !== "win") return;
    const count = reduced
      ? 20
      : Math.min(
          160,
          120 + (t > 2400 ? Math.floor(Math.min(4000, t - 2400) / 125) : 0),
        );
    for (let i = 0; i < count; i++) {
      const side = i % 2 === 0 ? 1 : -1,
        seed = (i * 0.6180339887) % 1,
        seconds = i < 120 ? t / 1000 : (t - (2400 + (i - 120) * 125)) / 1000;
      let x: number, y: number;
      if (reduced) {
        x = ((i * 0.754877) % 1) * width;
        y = ((i * 0.56984) % 1) * height;
      } else if (i >= 120) {
        x = seed * width;
        y = seconds * 80;
      } else {
        const drag = 0.6,
          g = 9,
          integral = (1 - Math.exp(-drag * seconds)) / drag;
        const up =
          (9 + seed * 5) * integral - (g / drag) * (seconds - integral);
        x =
          (side === 1 ? -12 : width + 12) +
          (side * (0.5 + ((i * 0.41421) % 1) * 2.4) * integral * 150 * width) /
            412;
        y = height - (up * 150 * height) / 915;
      }
      if (x < 0 || x > width || y < 0 || y > height) continue;
      const distance = 2,
        hh = Math.tan((camera.fov * Math.PI) / 360) * distance;
      dummy.position
        .set(
          ((x / width) * 2 - 1) * hh * camera.aspect,
          (1 - (y / height) * 2) * hh,
          -distance,
        )
        .applyMatrix4(camera.matrixWorld);
      dummy.quaternion.copy(camera.quaternion);
      dummy.rotateZ(reduced ? i : (t / 1000) * (2 + (i % 5)) * Math.PI * 2 + i);
      dummy.scale.set(
        ((3 + (i % 3)) * hh * 2) / height,
        ((2 + (i % 2)) * hh * 2) / height,
        1,
      );
      dummy.updateMatrix();
      this.confetti.setMatrixAt(this.confetti.count, dummy.matrix);
      this.confetti.setColorAt(
        this.confetti.count,
        new THREE.Color(
          [0xffd640, 0x2366ff, 0xff6b8a, 0x7be0a8, 0xb98bff][i % 5],
        ),
      );
      this.confetti.count++;
      this.visibleConfetti++;
    }
    this.confetti.instanceMatrix.needsUpdate = true;
    if (this.confetti.instanceColor)
      this.confetti.instanceColor.needsUpdate = true;
  }
}
